/**
 * app/providers/SentryProvider.tsx
 * Error boundary global. Sentry solo se inicializa si hay DSN configurado.
 */

import { Component, type ReactNode, type ErrorInfo } from 'react'

// Inicializar Sentry solo si hay DSN (evita error en desarrollo)
if (import.meta.env.VITE_SENTRY_DSN) {
  import('@sentry/react').then(Sentry => {
    Sentry.init({
      dsn:              import.meta.env.VITE_SENTRY_DSN as string,
      environment:      import.meta.env.MODE,
      release:          import.meta.env.VITE_APP_VERSION as string,
      tracesSampleRate: 0.1,
    })
  })
}

interface Props  { children: ReactNode }
interface State  { hasError: boolean; error: Error | null }

export class SentryProvider extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info)
    if (import.meta.env.VITE_SENTRY_DSN) {
      import('@sentry/react').then(Sentry =>
        Sentry.captureException(error, { extra: { componentStack: info.componentStack } })
      )
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen items-center justify-center bg-danger-50 p-6">
          <div className="text-center max-w-sm">
            <div className="text-5xl mb-4">⚠️</div>
            <h1 className="text-xl font-bold text-danger-700 mb-2">Error inesperado</h1>
            <p className="text-surface-600 text-sm mb-6">
              El sistema encontró un error. Por favor recarga la página.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-brand-600 text-white px-6 py-3 rounded-xl font-semibold"
            >
              Recargar
            </button>
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-4 text-left text-xs text-surface-500 bg-surface-100 p-3 rounded">
                <summary className="cursor-pointer font-medium">Detalle (dev)</summary>
                <pre className="mt-2 overflow-auto">{this.state.error.stack}</pre>
              </details>
            )}
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
